import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { Period, Reference, TimeSlot } from '@sonar/models';

import {
  customersCountsAggregatedRequested,
  revenuesAggregatedRequested,
} from '../Entities/actions';
import { areAverageSpentsLoading } from '../Entities/AverageSpents/selectors';
import {
  areAverageSpentsAggregatedLoading,
  getAverageSpentsAggregatedError,
  getEvolutionAverageSpentsAggregated,
  getReferenceAverageSpentsAggregated,
  getSelectedAverageSpentsAggregated,
} from '../Entities/AverageSpentsAggregated/selectors';
import {
  areCustomersCountsAggregatedLoading,
  getCustomersCountsAggregatedError,
  getEvolutionCustomersCountsAggregated,
  getReferenceCustomersCountsAggregated,
  getSelectedCustomersCountsAggregated,
} from '../Entities/CustomersCountsAggregated/selectors';
import {
  areRevenuesAggregatedLoading,
  getEvolutionRevenuesAggregated,
  getReferenceRevenuesAggregated,
  getRevenuesAggregatedError,
  getSelectedRevenuesAggregated,
} from '../Entities/RevenuesAggregated/selectors';
import { getKpisPerStore } from '../Entities/selectors';

interface UseLegacyOrdersKpiParam {
  period: Period;
  referenceCode: Reference;
  timeSlot: TimeSlot;
  trainingMode: boolean;
  canRequestReports: boolean;
}

function useLegacyOrdersKpi({
  period,
  referenceCode,
  timeSlot,
  trainingMode,
  canRequestReports,
}: UseLegacyOrdersKpiParam) {
  const periodAsString = period.serialize();
  const timeSlotAsString = timeSlot.serialize();
  const dispatch = useDispatch();

  useQuery(
    [
      'legacyRequestOrdersKpi',
      { canRequestReports, referenceCode, trainingMode, periodAsString, timeSlotAsString },
    ],
    () => {
      if (canRequestReports) {
        dispatch(revenuesAggregatedRequested());
        dispatch(customersCountsAggregatedRequested());
      }

      return true;
    },
    // override default value set in queryConfig
    // allways execute the query (but only once per page)
    // as the result is in the redux store
    // the api call will update the store state
    { staleTime: 0 },
  );

  const isLoading = useSelector(areAverageSpentsLoading);

  const averageSpentsKpi = {
    error: useSelector(getAverageSpentsAggregatedError),
    evolution: useSelector(getEvolutionAverageSpentsAggregated),
    isLoading: useSelector(areAverageSpentsAggregatedLoading),
    reference: useSelector(getReferenceAverageSpentsAggregated),
    selected: useSelector(getSelectedAverageSpentsAggregated),
  };
  const customersCountsKpi = {
    error: useSelector(getCustomersCountsAggregatedError),
    evolution: useSelector(getEvolutionCustomersCountsAggregated),
    isLoading: useSelector(areCustomersCountsAggregatedLoading),
    reference: useSelector(getReferenceCustomersCountsAggregated),
    selected: useSelector(getSelectedCustomersCountsAggregated),
  };
  const revenuesKpi = {
    error: useSelector(getRevenuesAggregatedError),
    evolution: useSelector(getEvolutionRevenuesAggregated),
    isLoading: useSelector(areRevenuesAggregatedLoading),
    reference: useSelector(getReferenceRevenuesAggregated),
    selected: useSelector(getSelectedRevenuesAggregated),
  };
  const kpisPerStore = useSelector(getKpisPerStore);

  return { isLoading, averageSpentsKpi, customersCountsKpi, revenuesKpi, kpisPerStore };
}

export default useLegacyOrdersKpi;
